/* organization/WorkspaceDetail — Workspace operational detail.
   Architecture: Workspace is a business unit, NOT a tenant.
   - Organization OWNS Members, Roles, Permissions, Subscription
   - Workspace MANAGES Assignments, Feature consumption, Operational settings
   Tabs: Overview · Assignments · Access · Modules · Settings · Usage · Health · Activity */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill, Modal, ConfirmDialog } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { StatCard, TabBar, Th } from '../platform/_ui';
import {
  useOrgRuntime,
  useOrganization,
  useLimits,
  useUsage,
  useOrgFeatures,
  useOrgPermissions,
  useCurrentOrgUser,
} from './OrganizationRuntimeContext.jsx';
import { useWsPermissions } from './auth/usePermissions.js';
import * as wsApi from '../api/workspace.api.js';
import * as orgApi from '../api/organization.api.js';
import * as auditApi from '../api/audit.api.js';

/* ── Tabs ────────────────────────────────────────────────────── */
const TABS = [
  { id: 'overview',     label: 'Overview',     icon: 'Home'        },
  { id: 'assignments',  label: 'Assignments',  icon: 'Users'       },
  { id: 'access',       label: 'Access',       icon: 'Shield'      },
  { id: 'modules',      label: 'Modules',      icon: 'Grid'        },
  { id: 'settings',     label: 'Settings',     icon: 'Settings'    },
  { id: 'usage',        label: 'Usage',        icon: 'BarChart2'   },
  { id: 'health',       label: 'Health',       icon: 'Activity'    },
  { id: 'activity',     label: 'Activity',     icon: 'Clock'       },
];

/* ── Workspace roles (backend-supported only) ────────────────── */
const WS_ROLES = [
  { value: 'WORKSPACE_ADMIN', label: 'Workspace Admin',   tone: 'purple', desc: 'Full workspace access — settings, assignments, all modules'  },
  { value: 'WORKSPACE_USER',  label: 'Workspace User',    tone: 'blue',   desc: 'Standard operational access — CRM, tasks, queues, pipelines' },
];
const WS_ROLE_MAP   = Object.fromEntries(WS_ROLES.map(r => [r.value, r]));

/* ── Capability resolution per wsRole ───────────────────────── */
const ROLE_CAPABILITIES = {
  WORKSPACE_ADMIN: [
    { key: 'workspace.view',     label: 'View Workspace',       granted: true  },
    { key: 'workspace.settings', label: 'Manage Settings',      granted: true  },
    { key: 'membership.view',    label: 'View Assignments',      granted: true  },
    { key: 'membership.manage',  label: 'Manage Assignments',    granted: true  },
    { key: 'crm.use',            label: 'Use CRM',              granted: true  },
    { key: 'crm.manage',         label: 'Manage CRM',           granted: true  },
  ],
  WORKSPACE_USER: [
    { key: 'workspace.view',     label: 'View Workspace',       granted: true  },
    { key: 'workspace.settings', label: 'Manage Settings',      granted: false },
    { key: 'membership.view',    label: 'View Assignments',      granted: true  },
    { key: 'membership.manage',  label: 'Manage Assignments',    granted: false },
    { key: 'crm.use',            label: 'Use CRM',              granted: true  },
    { key: 'crm.manage',         label: 'Manage CRM',           granted: false },
  ],
};

/* ── Module definitions (plan → feature prefix → workspace) ─── */
const MODULE_DEFS = [
  { key: 'CRM',        icon: 'Users2',       color: 'var(--nx-accent)', prefix: 'crm',       wsKeys: ['People','Companies','Pipeline','Tasks','Calendar','Notes'] },
  { key: 'Analytics',  icon: 'BarChart2',    color: 'var(--nx-accent)', prefix: 'analytics', wsKeys: [] },
  { key: 'Channels',   icon: 'MessageCircle',color: 'var(--nx-accent)', prefix: 'channels',  wsKeys: [] },
  { key: 'Storage',    icon: 'HardDrive',    color: 'var(--nx-accent)', prefix: 'storage',   wsKeys: [] },
  { key: 'AI',         icon: 'Cpu',          color: 'var(--nx-accent)', prefix: 'ai',        wsKeys: [] },
  { key: 'Campaigns',  icon: 'Mail',         color: 'var(--nx-accent)', prefix: 'campaigns', wsKeys: [] },
  { key: 'Broadcast',  icon: 'Radio',        color: 'var(--nx-accent)', prefix: 'broadcast', wsKeys: [] },
  { key: 'Knowledge',  icon: 'BookOpen',     color: 'var(--nx-accent)', prefix: 'knowledge', wsKeys: [] },
  { key: 'Reports',    icon: 'FileText',     color: 'var(--nx-accent)', prefix: 'reports',   wsKeys: [] },
];


const WS_STATUS_TONE  = { ACTIVE: 'green', ONBOARDING: 'blue', ARCHIVED: 'gray' };
const WS_STATUS_LABEL = { ACTIVE: 'Active', ONBOARDING: 'Onboarding', ARCHIVED: 'Archived' };

const GRADE_BG        = { green: 'var(--nx-success-tint, #dcfce7)', amber: 'var(--nx-warning-tint, #fef9c3)', red: 'var(--nx-danger-tint, #fee2e2)' };
const GRADE_COL       = { green: 'var(--nx-success)', amber: 'var(--nx-warning)', red: 'var(--nx-danger)' };
const GRADE_LABEL_MAP = { green: 'Healthy', amber: 'Warning', red: 'Critical' };
const GRADE_STATUS    = { green: 'Healthy', amber: 'Needs attention', red: 'Unhealthy' };
const GRADE_MSG       = {
  green: 'Healthy — all systems operational',
  amber: 'Needs attention — review checks below',
  red:   'Unhealthy — immediate action required',
};

function gradeFromScore(s) {
  if (s >= 75) return 'green';
  if (s >= 50) return 'amber';
  return 'red';
}

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

/* ── Health score computation ────────────────────────────────── */
function computeHealth(assignments, ws, usage, limits) {
  let score = 0;
  const checks = [];
  const memberCount = assignments.length;
  const moduleCount = (ws.modules ?? []).length;

  const hasAdmin = assignments.some(a => a.wsRole === 'WORKSPACE_ADMIN');
  if (hasAdmin) {
    score += 35;
    checks.push({ label: 'Workspace Admin assigned', ok: true });
  } else {
    checks.push({ label: 'No Workspace Admin assigned', ok: false });
  }

  const memberLabel = memberCount + ' member' + (memberCount === 1 ? '' : 's') + ' assigned';
  if (memberCount > 0) {
    score += 25;
    checks.push({ label: memberLabel, ok: true });
  } else {
    checks.push({ label: 'No members assigned', ok: false });
  }

  const moduleLabel = moduleCount + ' module' + (moduleCount === 1 ? '' : 's') + ' enabled';
  if (moduleCount > 0) {
    score += 25;
    checks.push({ label: moduleLabel, ok: true });
  } else {
    checks.push({ label: 'No modules enabled', ok: false });
  }

  const storagePct = limits.maxStorageGb > 0 ? usage.storageGb / limits.maxStorageGb : 0;
  if (storagePct < 0.9) {
    score += 15;
    checks.push({ label: 'Storage within healthy range', ok: true });
  } else {
    checks.push({ label: 'Storage nearly full', ok: false });
  }

  return { score, grade: gradeFromScore(score), checks };
}

/* ── Shared modal ────────────────────────────────────────────── */
/* ── Progress bar ────────────────────────────────────────────── */
function UsageBar({ label, used, max, unit, color, icon }) {
  const pct = max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
  let barColor = color;
  if (pct >= 100) barColor = 'var(--nx-danger)';
  else if (pct >= 80) barColor = 'var(--nx-warning)';
  const unlimited = max <= 0;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <NexusIcon name={icon} size={12} style={{ color }} />{label}
        </span>
        <span style={{ fontWeight: 700, color: 'var(--nx-text-muted)' }}>
          {unlimited ? used + ' / ∞' : used + ' / ' + max + ' ' + unit + ' (' + pct + '%)'}
        </span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: 'var(--nx-border)' }}>
        <div style={{ height: '100%', borderRadius: 3, width: unlimited ? '10%' : pct + '%', background: barColor, transition: 'width .3s' }} />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/* SECTION 1 — Overview                                            */
/* ─────────────────────────────────────────────────────────────── */
function OverviewTab({ ws, assignments, usage, limits, org }) {
  const { orgId }    = useParams();
  const navigate     = useNavigate();
  const primaryAdmin = assignments.find(a => a.wsRole === 'WORKSPACE_ADMIN');
  const health       = computeHealth(assignments, ws, usage, limits);

  let createdFrom;
  if (ws.provisionedFromTemplateId) {
    createdFrom = (
      <button
        type="button"
        onClick={() => navigate(`/organization/${orgId}/workspaces?tab=templates&templateId=${ws.provisionedFromTemplateId}`)}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--nx-accent)', fontSize: 12, textDecoration: 'underline' }}
      >
        {ws.provisionedFromTemplateName}
      </button>
    );
  } else if (ws.provisionedFromTemplateName) {
    createdFrom = <span style={{ color: 'var(--nx-text-muted)' }}>{ws.provisionedFromTemplateName} (deleted)</span>;
  } else {
    createdFrom = 'Blank Workspace';
  }

  const infoRows = [
    ['Workspace ID',   ws.id],
    ['Organization',   org?.name ?? '—'],
    ['Status',         WS_STATUS_LABEL[ws.status] ?? ws.status],
    ['Created',        fmt(ws.createdAt)],
    ['Last Updated',   fmt(ws.updatedAt)],
    ['Primary Admin',  primaryAdmin?.member?.name ?? primaryAdmin?.member?.email ?? 'Not assigned'],
    ['Created From',   createdFrom],
    ['Modules Active', (ws.modules ?? []).length + ' of ' + MODULE_DEFS.length],
  ];

  return (
    <div>
      <div className="nx-cc-stat-grid nx-kpi-4" style={{ marginBottom: 16 }}>
        <StatCard label="Assigned"   value={assignments.length}                     icon="Users"    color="blue"   />
        <StatCard label="Status"     value={WS_STATUS_LABEL[ws.status] ?? ws.status} icon="Activity" color={WS_STATUS_TONE[ws.status] ?? 'gray'} />
        <StatCard label="Modules"    value={(ws.modules ?? []).length}              icon="Grid"     color="purple" />
        <StatCard label="Health"     value={health.score + '/100'}                  icon="Heart"    color={health.grade}   />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, maxWidth: 900, alignItems: 'start' }}>
        {/* Workspace Info */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
            Workspace Info
          </div>
          <div className="nx-form">
            {infoRows.map(([label, value]) => (
              <div key={label} className="nx-form-row" style={{ padding: '7px 0', borderBottom: '1px solid var(--nx-border)' }}>
                <label style={{ fontSize: 12, color: 'var(--nx-text-muted)', minWidth: 130, flexShrink: 0 }}>{label}</label>
                <span style={{ fontSize: 12, fontFamily: label === 'Workspace ID' ? 'monospace' : undefined, wordBreak: 'break-all' }}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Workspace Health Summary */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
            Workspace Health
          </div>
          <div style={{ padding: '14px 16px', borderRadius: 10, border: '1.5px solid var(--nx-border)', background: 'var(--nx-surface)', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ width: 48, height: 48, borderRadius: 24, background: GRADE_BG[health.grade], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: GRADE_COL[health.grade] }}>{health.score}</span>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>Health Score</div>
                <div className="nx-muted nx-mini">{GRADE_STATUS[health.grade]}</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {health.checks.map(c => (
                <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12 }}>
                  <NexusIcon name={c.ok ? 'CheckCircle' : 'AlertCircle'} size={13} style={{ color: c.ok ? 'var(--nx-success)' : 'var(--nx-warning)', flexShrink: 0 }} />
                  <span style={{ color: c.ok ? 'var(--nx-text)' : 'var(--nx-text-muted)' }}>{c.label}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Storage usage mini bar */}
          <UsageBar
            label="Organization Storage"
            used={usage.storageGb ?? 0}
            max={limits.maxStorageGb ?? 0}
            unit="GB"
            color="var(--nx-accent)"
            icon="HardDrive"
          />
        </div>
      </div>
    </div>
  );
}

/* Shared role-change helper used by AssignmentsTab and AccessTab */
async function execRoleChange({ wsId, assignment, newRole, toast, onDone, onFinally }) {
  try {
    await wsApi.updateMember(wsId, assignment.memberId, { wsRole: newRole });
    toast({ tone: 'success', icon: 'Shield', title: 'Role updated', desc: WS_ROLE_MAP[newRole]?.label ?? newRole });
    onDone();
  } catch (err) {
    toast({ tone: 'error', icon: 'AlertCircle', title: 'Role change failed', desc: err?.message });
  } finally {
    onFinally?.();
  }
}

/* ─────────────────────────────────────────────────────────────── */
/* SECTION 2 — Assignments                                         */
/* ─────────────────────────────────────────────────────────────── */
function AssignmentsTab({ wsId, orgId, assignments, loadingAs }) {
  const navigate = useNavigate();
  const iamPath = `/organization/${orgId}/iam?tab=workspace-access&wsId=${wsId}`;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Workspace Assignments</div>
          <div className="nx-muted nx-mini">
            Assignment management is owned by Identity &amp; Access. This view is read-only.
          </div>
        </div>
        <Btn variant="secondary" icon="Lock" onClick={() => navigate(iamPath)}>
          Manage in Identity &amp; Access
        </Btn>
      </div>

      {loadingAs && (
        <div className="nx-empty"><NexusIcon name="Loader" size={18} /><span>Loading assignments…</span></div>
      )}
      {!loadingAs && assignments.length === 0 && (
        <div className="nx-empty">
          <NexusIcon name="Users" size={22} />
          <span>No members assigned to this workspace.</span>
          <Btn variant="secondary" icon="Lock" onClick={() => navigate(iamPath)}>Manage in Identity &amp; Access</Btn>
        </div>
      )}
      {!loadingAs && assignments.length > 0 && (
        <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
          <thead><tr>
            <Th icon="User">Member</Th>
            <Th icon="Mail">Email</Th>
            <Th icon="Shield">Workspace Role</Th>
            <Th icon="Building2">Org Role</Th>
            <Th icon="Calendar">Assigned</Th>
          </tr></thead>
          <tbody>{assignments.map(a => (
            <tr key={a.id} className="nx-tr">
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                    {(a.member?.name ?? a.member?.email ?? '?')[0].toUpperCase()}
                  </div>
                  <span className="nx-row-name-text">{a.member?.name ?? '—'}</span>
                </div>
              </td>
              <td><span className="nx-muted" style={{ fontSize: 12 }}>{a.member?.email ?? '—'}</span></td>
              <td>
                <Pill tone={WS_ROLE_MAP[a.wsRole]?.tone ?? 'gray'}>
                  {WS_ROLE_MAP[a.wsRole]?.label ?? a.wsRole ?? '—'}
                </Pill>
              </td>
              <td><span className="nx-muted nx-mini">{(a.member?.orgRole ?? '').split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')}</span></td>
              <td><span className="nx-muted nx-mini">{fmt(a.createdAt)}</span></td>
            </tr>
          ))}</tbody>
        </table></div></div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/* SECTION 3 + 4 — Access (Roles + Resolution)                     */
/* ─────────────────────────────────────────────────────────────── */
function AccessTab({ wsId, orgId, assignments, wsPerm, onAssignmentsChange }) {
  const toast    = useToast();
  const navigate = useNavigate();
  const [selectedRole,  setSelectedRole]  = useState('WORKSPACE_ADMIN');
  const [changingRole,  setChangingRole]  = useState(null);
  const caps = ROLE_CAPABILITIES[selectedRole] ?? [];

  const handleChangeRole = (a, newRole) => {
    setChangingRole(null);
    execRoleChange({ wsId, assignment: a, newRole, toast, onDone: onAssignmentsChange });
  };

  return (
    <div>
      {orgId && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <button
            className="nx-btn nx-btn--ghost nx-btn--sm"
            onClick={() => navigate(`/organization/${orgId}/iam?tab=workspace-access`)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}
          >
            <NexusIcon name="Lock" size={13} />
            Manage in Identity &amp; Access
          </button>
        </div>
      )}
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, maxWidth: 900, alignItems: 'start' }}>
      {/* Role definitions */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>
          Workspace Roles
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {WS_ROLES.map(r => (
            <button key={r.value} type="button"
              onClick={() => setSelectedRole(r.value)}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px', borderRadius: 8, border: `1.5px solid ${selectedRole === r.value ? 'var(--nx-accent)' : 'var(--nx-border)'}`, background: selectedRole === r.value ? 'var(--nx-accent-tint)' : 'var(--nx-surface)', cursor: 'pointer', transition: 'all .15s' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                <Pill tone={r.tone}>{r.label}</Pill>
              </div>
              <div className="nx-muted nx-mini">{r.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Capability resolution + live assignments */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>
          Access Resolution — {WS_ROLE_MAP[selectedRole]?.label}
        </div>
        <div style={{ padding: '12px 14px', borderRadius: 8, border: '1.5px solid var(--nx-border)', background: 'var(--nx-surface)', marginBottom: 14 }}>
          <div className="nx-muted nx-mini" style={{ marginBottom: 10 }}>
            Permissions granted to members assigned this role.
          </div>
          {caps.map(c => (
            <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid var(--nx-border)' }}>
              <NexusIcon name={c.granted ? 'CheckCircle' : 'XCircle'} size={14}
                style={{ color: c.granted ? 'var(--nx-success)' : 'var(--nx-text-faint)', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: c.granted ? 'var(--nx-text)' : 'var(--nx-text-muted)' }}>{c.label}</span>
            </div>
          ))}
        </div>

        {assignments.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
              Current Assignments
            </div>
            {assignments.map(a => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid var(--nx-border)', fontSize: 12 }}>
                <span style={{ flex: 1, fontWeight: 500 }}>{a.member?.name ?? a.member?.email ?? '—'}</span>
                {wsPerm?.isAdmin && changingRole === a.id ? (
                  <select className="nx-input" style={{ fontSize: 11, width: 150 }}
                    defaultValue={a.wsRole ?? 'WORKSPACE_USER'}
                    onChange={e => handleChangeRole(a, e.target.value)}
                    onBlur={() => setChangingRole(null)}>
                    {WS_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Pill tone={WS_ROLE_MAP[a.wsRole]?.tone ?? 'gray'}>{WS_ROLE_MAP[a.wsRole]?.label ?? a.wsRole}</Pill>
                    {wsPerm?.isAdmin && (
                      <NexusIcon name="Pencil" size={11} style={{ color: 'var(--nx-text-faint)', cursor: 'pointer' }}
                        onClick={() => setChangingRole(a.id)} title="Change role" />
                    )}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    </div>
  );
}

const MOD_STATUS_TONE   = { enabled: 'green', disabled: 'gray', unavailable: 'red' };
const MOD_STATUS_LABEL  = { enabled: 'Enabled', disabled: 'Disabled', unavailable: 'Unavailable' };
const MOD_STATUS_REASON = {
  unavailable: 'Not included in your plan',
  disabled:    'Disabled on this workspace',
  enabled:     'Active on this workspace',
};

function getModuleStatus(mod, featureSet, features, localModules) {
  const hasFeature = featureSet.has(mod.prefix) || features.some(f => f.startsWith(mod.prefix + '.'));
  if (!hasFeature) return 'unavailable';
  if (mod.wsKeys.length === 0) return 'enabled';
  return mod.wsKeys.some(k => localModules.includes(k)) ? 'enabled' : 'disabled';
}

function computeUpdatedModules(wsKeys, current) {
  if (wsKeys.length === 0) return current;
  const allEnabled = wsKeys.every(k => current.includes(k));
  return allEnabled
    ? current.filter(k => !wsKeys.includes(k))
    : [...new Set([...current, ...wsKeys])];
}

function ModuleRow({ mod, status, saving, canToggle, onToggle, navigate, orgId }) {
  const enabled      = status === 'enabled';
  const available    = status !== 'unavailable';
  const iconBg       = enabled ? mod.color + '18' : 'var(--nx-surface-alt)';
  const iconColor    = enabled ? mod.color : 'var(--nx-text-faint)';
  const showToggle   = canToggle && available && mod.wsKeys.length > 0;
  const toggleLeft   = enabled ? 21 : 3;
  const toggleBg     = enabled ? 'var(--nx-success)' : 'var(--nx-border)';
  const toggleCursor = saving ? 'default' : 'pointer';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 8, border: '1.5px solid var(--nx-border)', background: 'var(--nx-surface)', opacity: available ? 1 : 0.55 }}>
      <div style={{ width: 34, height: 34, borderRadius: 8, background: iconBg, color: iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <NexusIcon name={mod.icon} size={16} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{mod.key}</div>
        <div className="nx-muted nx-mini">{MOD_STATUS_REASON[status]}</div>
      </div>
      <Pill tone={MOD_STATUS_TONE[status]}>{MOD_STATUS_LABEL[status]}</Pill>
      {showToggle && (
        <button type="button" disabled={saving} onClick={() => onToggle(mod)}
          style={{ width: 40, height: 22, borderRadius: 11, border: 'none', cursor: toggleCursor, background: toggleBg, position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
          title={enabled ? 'Disable ' + mod.key : 'Enable ' + mod.key}>
          <span style={{ position: 'absolute', top: 3, left: toggleLeft, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,.25)' }} />
        </button>
      )}
      {!available && (
        <button type="button" onClick={() => navigate(`/organization/${orgId}/licenses`)}
          style={{ fontSize: 11, color: 'var(--nx-accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
          Upgrade plan
        </button>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/* SECTION 5 — Modules                                             */
/* ─────────────────────────────────────────────────────────────── */
function ModulesTab({ wsId, orgId, ws, features, wsPerm, onModulesChange }) {
  const toast    = useToast();
  const navigate = useNavigate();
  const [localModules, setLocalModules] = useState(ws.modules ?? []);
  const [saving,       setSaving]       = useState(false);

  useEffect(() => { setLocalModules(ws.modules ?? []); }, [ws.id, ws.modules]);

  const featureSet = new Set(features);

  const handleToggle = async (mod) => {
    const prev    = localModules;
    const updated = computeUpdatedModules(mod.wsKeys, prev);
    setLocalModules(updated);
    setSaving(true);
    try {
      await orgApi.updateWorkspace(orgId, wsId, { modules: updated });
      toast({ tone: 'success', icon: 'Check', title: mod.key + ' updated' });
      onModulesChange();
    } catch (err) {
      setLocalModules(prev);
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Module update failed', desc: err?.message });
    } finally { setSaving(false); }
  };

  return (
    <div style={{ maxWidth: 680 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Module Access</div>
          <div className="nx-muted nx-mini">
            Module availability is resolved from Plan → Organization Features. Workspace cannot enable unavailable modules.
          </div>
        </div>
        {saving && <span className="nx-muted nx-mini"><NexusIcon name="Loader" size={12} /> Saving…</span>}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {MODULE_DEFS.map(mod => (
          <ModuleRow
            key={mod.key}
            mod={mod}
            status={getModuleStatus(mod, featureSet, features, localModules)}
            saving={saving}
            canToggle={Boolean(wsPerm?.isAdmin)}
            onToggle={handleToggle}
            navigate={navigate}
            orgId={orgId}
          />
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/* SECTION 6 — Operational Settings                                */
/* ─────────────────────────────────────────────────────────────── */
function SettingsTab({ ws, orgId, wsPerm, onSaved }) {
  const toast    = useToast();
  const [name,   setName]   = useState(ws.name ?? '');
  const [busy,   setBusy]   = useState(false);

  useEffect(() => { setName(ws.name ?? ''); }, [ws.id, ws.name]);

  const isDirty = name.trim() !== (ws.name ?? '');

  const handleSave = async () => {
    if (!name.trim() || !isDirty) return;
    setBusy(true);
    try {
      await orgApi.updateWorkspace(orgId, ws.id, { name: name.trim() });
      toast({ tone: 'success', icon: 'Check', title: 'Settings saved' });
      onSaved();
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Save failed', desc: err?.message });
    } finally { setBusy(false); }
  };

  const metaRows = [
    { label: 'Workspace ID',  value: ws.id,                                    mono: true  },
    { label: 'Status',        value: WS_STATUS_LABEL[ws.status] ?? ws.status,  mono: false },
    { label: 'Created',       value: fmt(ws.createdAt),                         mono: false },
    { label: 'Last Updated',  value: fmt(ws.updatedAt),                         mono: false },
  ];

  return (
    <div style={{ maxWidth: 560 }}>
      <div style={{ padding: '8px 12px', borderRadius: 6, background: 'var(--nx-accent-tint)', fontSize: 12, color: 'var(--nx-accent)', marginBottom: 18 }}>
        Workspace settings only. Branding, white-label, subscription, and billing are managed at the Organization level.
      </div>

      {/* Editable field */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>
        Display Name
      </div>
      <div className="nx-form" style={{ gap: 10, marginBottom: 18 }}>
        <div className="nx-form-row">
          <label htmlFor="ws-settings-name">Workspace Name <span style={{ color: 'var(--nx-danger)' }}>*</span></label>
          <input id="ws-settings-name" className="nx-input" value={name} disabled={!wsPerm?.isAdmin}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
            placeholder="Workspace name" />
        </div>
      </div>

      {wsPerm?.isAdmin ? (
        <Btn variant="primary" icon="Check" disabled={busy || !name.trim() || !isDirty} onClick={handleSave}>
          {busy ? 'Saving…' : 'Save Changes'}
        </Btn>
      ) : (
        <p className="nx-muted nx-mini" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <NexusIcon name="Lock" size={11} /> Settings can only be changed by Workspace Admins and Org Admins.
        </p>
      )}

      {/* Read-only metadata */}
      <div style={{ marginTop: 24, borderTop: '1px solid var(--nx-border)', paddingTop: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>
          Workspace Information
        </div>
        <div>
          {metaRows.map(r => (
            <div key={r.label} style={{ display: 'flex', alignItems: 'baseline', padding: '6px 0', borderBottom: '1px solid var(--nx-border)', fontSize: 12 }}>
              <span style={{ color: 'var(--nx-text-muted)', width: 130, flexShrink: 0 }}>{r.label}</span>
              <span style={{ fontFamily: r.mono ? 'monospace' : undefined, wordBreak: 'break-all', color: 'var(--nx-text)' }}>{r.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/* SECTION 7 — Usage                                               */
/* ─────────────────────────────────────────────────────────────── */
function UsageTab({ usage, limits, assignments }) {
  const dims = [
    { label: 'Assigned Members', used: assignments.length, max: limits.maxUsers ?? 0,          unit: 'seats',    icon: 'Users',        color: 'var(--nx-accent)' },
    { label: 'Storage',          used: usage.storageGb ?? 0, max: limits.maxStorageGb ?? 0,    unit: 'GB',       icon: 'HardDrive',    color: 'var(--nx-accent)' },
    { label: 'API Calls (Today)', used: usage.apiCallsToday ?? 0, max: limits.maxApiCallsPerDay ?? 0, unit: 'calls', icon: 'Zap',     color: 'var(--nx-accent)' },
    { label: 'Messages (Month)', used: usage.messagesThisMonth ?? 0, max: limits.maxMessagesPerMonth ?? 0, unit: 'msgs', icon: 'MessageSquare', color: 'var(--nx-accent)' },
    { label: 'AI Agents',        used: usage.agents ?? 0,    max: limits.maxAgents ?? 0,        unit: 'agents',   icon: 'Cpu',          color: 'var(--nx-accent)' },
    { label: 'Bots',             used: usage.bots ?? 0,      max: limits.maxBots ?? 0,          unit: 'bots',     icon: 'Bot',          color: 'var(--nx-accent)' },
  ];

  return (
    <div style={{ maxWidth: 620 }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>Resource Usage</div>
        <div className="nx-muted nx-mini">
          Usage is tracked at the Organization level. Workspace assignments consume from the Organization's subscription limits.
        </div>
      </div>
      <div style={{ padding: '16px 18px', borderRadius: 10, border: '1.5px solid var(--nx-border)', background: 'var(--nx-surface)' }}>
        {dims.map(d => (
          <UsageBar key={d.label} label={d.label} used={d.used} max={d.max} unit={d.unit} icon={d.icon} color={d.color} />
        ))}
      </div>
      <div className="nx-muted nx-mini" style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 5 }}>
        <NexusIcon name="Info" size={12} />
        Limits are set by your subscription plan. Contact the Control Center to upgrade.
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/* SECTION 8 — Health                                              */
/* ─────────────────────────────────────────────────────────────── */
function HealthTab({ ws, assignments, usage, limits, features, onNavigateAssignments, onNavigateModules }) {
  const health = computeHealth(assignments, ws, usage, limits);

  const featureCount   = features.length;
  const moduleCount    = (ws.modules ?? []).length;
  const adminCount     = assignments.filter(a => a.wsRole === 'WORKSPACE_ADMIN').length;
  const userCount      = assignments.filter(a => a.wsRole === 'WORKSPACE_USER').length;
  const storagePct     = limits.maxStorageGb > 0 ? Math.round((usage.storageGb / limits.maxStorageGb) * 100) : 0;

  const sections = [
    {
      label: 'Assignments',
      icon: 'Users',
      action: assignments.length === 0 || adminCount === 0 ? { label: 'Go to Assignments', fn: onNavigateAssignments } : null,
      items: [
        { label: 'Total assignments', value: assignments.length, ok: assignments.length > 0 },
        { label: 'Workspace Admins',  value: adminCount,         ok: adminCount > 0 },
        { label: 'Workspace Users',   value: userCount,          ok: true           },
      ],
    },
    {
      label: 'Modules',
      icon: 'Grid',
      action: moduleCount === 0 ? { label: 'Go to Modules', fn: onNavigateModules } : null,
      items: [
        { label: 'Modules enabled',    value: moduleCount,   ok: moduleCount > 0   },
        { label: 'Features available', value: featureCount,  ok: featureCount > 0  },
      ],
    },
    {
      label: 'Storage',
      icon: 'HardDrive',
      action: null,
      items: [
        { label: 'Storage used',  value: (usage.storageGb ?? 0) + ' GB',                                         ok: storagePct < 80 },
        { label: 'Storage quota', value: limits.maxStorageGb > 0 ? limits.maxStorageGb + ' GB' : 'Unlimited',    ok: true            },
      ],
    },
  ];

  return (
    <div style={{ maxWidth: 700 }}>
      {/* Score card */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px', borderRadius: 10, border: '1.5px solid var(--nx-border)', background: 'var(--nx-surface)', marginBottom: 20 }}>
        <div style={{ width: 64, height: 64, borderRadius: 32, background: GRADE_BG[health.grade], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span style={{ fontSize: 22, fontWeight: 800, color: GRADE_COL[health.grade] }}>{health.score}</span>
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Workspace Health Score</div>
          <div className="nx-muted nx-mini">{GRADE_MSG[health.grade]}</div>
        </div>
        <Pill tone={health.grade} style={{ marginLeft: 'auto' }}>
          {GRADE_LABEL_MAP[health.grade]}
        </Pill>
      </div>

      {/* Health check cards — auto-fit so 3 cards spread evenly */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
        {sections.map(s => (
          <div key={s.label} style={{ padding: '14px 16px', borderRadius: 8, border: '1.5px solid var(--nx-border)', background: 'var(--nx-surface)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
              <NexusIcon name={s.icon} size={14} style={{ color: 'var(--nx-accent)' }} />
              <span style={{ fontSize: 12, fontWeight: 700 }}>{s.label}</span>
            </div>
            <div style={{ flex: 1 }}>
              {s.items.map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '4px 0', borderBottom: '1px solid var(--nx-border)', fontSize: 12 }}>
                  <NexusIcon name={item.ok ? 'CheckCircle' : 'AlertCircle'} size={12}
                    style={{ color: item.ok ? 'var(--nx-success)' : 'var(--nx-warning)', flexShrink: 0 }} />
                  <span className="nx-muted">{item.label}</span>
                  <span style={{ marginLeft: 'auto', fontWeight: 600 }}>{item.value}</span>
                </div>
              ))}
            </div>
            {s.action && (
              <button onClick={s.action.fn}
                style={{ marginTop: 10, fontSize: 11, color: 'var(--nx-accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 4 }}>
                {s.action.label} <NexusIcon name="ArrowRight" size={10} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/* SECTION 9 — Activity                                            */
/* ─────────────────────────────────────────────────────────────── */
const SEV_COLOR = { warning: 'var(--nx-warning)', error: 'var(--nx-danger)' };

const WS_ACTION_META = {
  'created workspace':          { label: 'Workspace Created',          icon: 'Plus',           sev: 'info'    },
  'updated workspace settings': { label: 'Settings Updated',           icon: 'Settings',       sev: 'info'    },
  'archived workspace':         { label: 'Workspace Archived',         icon: 'Archive',        sev: 'warning' },
  'restored workspace':         { label: 'Workspace Restored',         icon: 'ArchiveRestore', sev: 'info'    },
};

function fmtAbsTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatWho(email) {
  if (!email) return 'System';
  if (!email.includes('@')) return email;
  const local = email.split('@')[0];
  return local.split(/[._-]/).map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(' ');
}

function ActivityTab({ orgId, wsId, wsName }) {
  const navigate = useNavigate();
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = useCallback(async () => {
    if (!orgId || !wsName) return;
    setLoading(true);
    setError(null);
    try {
      const res = await auditApi.listByOrg(orgId, { cats: 'Workspace', target: wsName, limit: 50 });
      setEvents(Array.isArray(res) ? res : (res?.data ?? []));
    } catch (err) {
      setError(err?.message || 'Failed to load activity');
    } finally { setLoading(false); }
  }, [orgId, wsName]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="nx-empty">
        <NexusIcon name="Loader" size={18} /><span>Loading activity…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="nx-empty">
        <NexusIcon name="AlertCircle" size={20} style={{ color: 'var(--nx-danger)' }} />
        <span style={{ color: 'var(--nx-danger)' }}>{error}</span>
        <Btn variant="secondary" icon="RefreshCw" onClick={load}>Retry</Btn>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="nx-empty" style={{ flexDirection: 'column', gap: 10, padding: '40px 0' }}>
        <NexusIcon name="Clock" size={24} style={{ color: 'var(--nx-text-faint)' }} />
        <span style={{ fontWeight: 600 }}>No activity recorded yet</span>
        <p className="nx-muted nx-mini" style={{ textAlign: 'center', maxWidth: 320 }}>
          Actions performed on this workspace — including creation, settings changes, and archiving — will appear here.
        </p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 680 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Workspace Activity</div>
          <div className="nx-muted nx-mini">{events.length} event{events.length !== 1 ? 's' : ''} recorded</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="ghost" size="sm" icon="RefreshCw" onClick={load}>Refresh</Btn>
          <Btn variant="ghost" size="sm" icon="ExternalLink"
            onClick={() => navigate('/organization/' + orgId + '/audit')}>
            Full Audit Log
          </Btn>
        </div>
      </div>

      <div style={{ border: '1.5px solid var(--nx-border)', borderRadius: 10, overflow: 'hidden', background: 'var(--nx-surface)' }}>
        {events.map((e, i) => {
          const meta     = WS_ACTION_META[e.action] ?? { label: e.action, icon: 'ClipboardList', sev: 'info' };
          const sevColor = SEV_COLOR[meta.sev] ?? 'var(--nx-accent)';
          const isLast   = i === events.length - 1;
          return (
            <div key={e.id ?? i} style={{ display: 'flex', gap: 12, padding: '12px 16px', borderBottom: isLast ? 'none' : '1px solid var(--nx-border)' }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: sevColor + '14', border: '1.5px solid ' + sevColor + '40', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                <NexusIcon name={meta.icon} size={14} style={{ color: sevColor }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>{meta.label}</span>
                  <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {e.time ?? fmtAbsTime(e.createdAt)}
                  </span>
                </div>
                {meta.sev === 'warning' && (
                  <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, background: 'var(--nx-warning)18', color: 'var(--nx-warning)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                    warning
                  </span>
                )}
                <div style={{ marginTop: 4, fontSize: 11, color: 'var(--nx-text-muted)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {e.who && (
                    <span title={e.who} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <NexusIcon name="User" size={10} style={{ flexShrink: 0 }} />{formatWho(e.who)}
                    </span>
                  )}
                  {e.ip && (
                    <span title={'Source: ' + e.ip} style={{ display: 'flex', alignItems: 'center', gap: 4, opacity: 0.6, fontFamily: 'monospace' }}>
                      <NexusIcon name="Globe" size={10} style={{ flexShrink: 0 }} />{e.ip}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/* MAIN COMPONENT                                                   */
/* ─────────────────────────────────────────────────────────────── */
export default function WorkspaceDetail() {
  const { orgId, wsId } = useParams();
  const navigate        = useNavigate();
  const toast           = useToast();
  const org             = useOrganization();
  const limits          = useLimits();
  const usage           = useUsage();
  const features        = useOrgFeatures();
  const permissions     = useOrgPermissions();
  const currentUser     = useCurrentOrgUser();
  const { invalidateRuntime } = useOrgRuntime();

  const auth           = window.useAuth?.();
  const [tab,          setTab]          = useState('overview');
  const [ws,           setWs]           = useState(null);
  const [loadingWs,    setLoadingWs]    = useState(true);
  const [wsError,      setWsError]      = useState(null);
  const [assignments,  setAssignments]  = useState([]);
  const [loadingAs,    setLoadingAs]    = useState(false);
  const [archiveConfirm,  setArchiveConfirm]  = useState(false);
  const [restoreConfirm,  setRestoreConfirm]  = useState(false);
  const [restoreBusy,     setRestoreBusy]     = useState(false);
  const [editModal,    setEditModal]    = useState(false);
  const [editName,     setEditName]     = useState('');
  const [editBusy,     setEditBusy]     = useState(false);

  const canManage = Boolean(permissions.canManageWorkspaces);

  const loadWs = useCallback(async () => {
    if (!wsId) return;
    setLoadingWs(true);
    setWsError(null);
    try {
      const data = await wsApi.getOne(wsId);
      setWs(data);
    } catch (err) {
      setWsError(err?.message || 'Failed to load workspace');
    } finally { setLoadingWs(false); }
  }, [wsId]);

  const loadAssignments = useCallback(async () => {
    if (!wsId) return;
    setLoadingAs(true);
    try {
      const res = await wsApi.listMembers(wsId);
      setAssignments(Array.isArray(res) ? res : (res?.data ?? []));
    } catch {
      setAssignments([]);
    } finally { setLoadingAs(false); }
  }, [wsId]);

  useEffect(() => { loadWs(); }, [loadWs]);
  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  const myAssignment    = assignments.find(a => a.member?.userId === currentUser?.id);
  const effectiveWsRole = canManage ? 'WORKSPACE_ADMIN' : myAssignment?.wsRole;
  const wsPerm          = useWsPermissions(effectiveWsRole, myAssignment?.wsCustomRole);

  /* ── Open in CRM ─────────────────────────────────────── */
  const handleOpenCRM = async () => {
    try {
      if (auth?.switchWorkspace) await auth.switchWorkspace(wsId);
    } catch { /* best-effort session update */ }
    window.location.replace('/app/' + wsId);
  };

  /* ── Edit/Rename ─────────────────────────────────────── */
  const handleEdit = async () => {
    if (!editName.trim()) return;
    setEditBusy(true);
    try {
      await orgApi.updateWorkspace(orgId, wsId, { name: editName.trim() });
      toast({ tone: 'success', icon: 'Check', title: 'Workspace renamed', desc: editName.trim() });
      setEditModal(false);
      loadWs();
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Rename failed', desc: err?.message });
    } finally { setEditBusy(false); }
  };

  /* ── Restore ────────────────────────────────────────── */
  const handleRestore = async () => {
    setRestoreBusy(true);
    try {
      await orgApi.restoreWorkspace(orgId, wsId);
      toast({ tone: 'success', icon: 'ArchiveRestore', title: 'Workspace restored', desc: ws?.name });
      setRestoreConfirm(false);
      invalidateRuntime(orgId);
      loadWs();
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Restore failed', desc: err?.message });
    } finally { setRestoreBusy(false); }
  };

  /* ── Archive ─────────────────────────────────────────── */
  const handleArchive = async () => {
    try {
      await orgApi.archiveWorkspace(orgId, wsId);
      toast({ tone: 'warning', icon: 'Archive', title: 'Workspace archived', desc: ws?.name });
      setArchiveConfirm(false);
      invalidateRuntime(orgId);
      navigate('/organization/' + orgId + '/workspaces');
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Archive failed', desc: err?.message });
    }
  };

  /* ── Loading / Error ─────────────────────────────────── */
  if (loadingWs) {
    return (
      <div className="nx-cc-page">
        <button className="nx-cc-back" onClick={() => navigate('/organization/' + orgId + '/workspaces')}>
          <NexusIcon name="ChevronLeft" size={14} /> Back to Workspaces
        </button>
        <div className="nx-empty" style={{ marginTop: 40 }}>
          <NexusIcon name="Loader" size={22} /><span>Loading workspace…</span>
        </div>
      </div>
    );
  }

  if (wsError || !ws) {
    return (
      <div className="nx-cc-page">
        <button className="nx-cc-back" onClick={() => navigate('/organization/' + orgId + '/workspaces')}>
          <NexusIcon name="ChevronLeft" size={14} /> Back to Workspaces
        </button>
        <div className="nx-empty" style={{ marginTop: 40 }}>
          <NexusIcon name="AlertCircle" size={22} style={{ color: 'var(--nx-danger)' }} />
          <span>{wsError ?? 'Workspace not found.'}</span>
          <Btn variant="secondary" icon="RefreshCw" onClick={loadWs}>Retry</Btn>
        </div>
      </div>
    );
  }

  const isArchived = ws.status === 'ARCHIVED';

  return (
    <div className="nx-cc-page">
      <button className="nx-cc-back" onClick={() => navigate('/organization/' + orgId + '/workspaces')}>
        <NexusIcon name="ChevronLeft" size={14} /> Back to Workspaces
      </button>

      {/* Hero */}
      <div className="nx-cc-org-hero">
        <div className="nx-cc-org-logo" style={{ background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)', fontSize: 18 }}>
          {(ws.name || 'W')[0].toUpperCase()}
        </div>
        <div className="nx-cc-org-meta">
          <h2 className="nx-cc-page-title">{ws.name}</h2>
          <div className="nx-cc-org-meta-row">
            <Pill tone={WS_STATUS_TONE[ws.status] ?? 'gray'}>{WS_STATUS_LABEL[ws.status] ?? ws.status}</Pill>
            <span className="nx-muted nx-mini">{assignments.length} assigned · {(ws.modules ?? []).length} modules</span>
            <span className="nx-muted nx-mini">Created {fmt(ws.createdAt)}</span>
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Btn variant="primary" size="sm" icon="ExternalLink"
            disabled={isArchived} title={isArchived ? 'Archived workspaces cannot be opened' : undefined}
            onClick={handleOpenCRM}>
            Open in CRM
          </Btn>
          {wsPerm?.isAdmin && (
            <>
              <Btn variant="ghost" size="sm" icon="Pencil"
                onClick={() => { setEditName(ws.name); setEditModal(true); }}>Rename</Btn>
              {!isArchived && (
                <Btn variant="ghost" size="sm" icon="Archive"
                  onClick={() => setArchiveConfirm(true)}>Archive</Btn>
              )}
            </>
          )}
        </div>
      </div>

      {isArchived && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderRadius: 8, background: 'var(--nx-surface-alt)', marginBottom: 12, fontSize: 13, color: 'var(--nx-text-muted)' }}>
          <NexusIcon name="Archive" size={14} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1 }}>This workspace is archived. All operations are disabled.</span>
          {canManage && (
            <Btn variant="ghost" size="sm" icon="ArchiveRestore" onClick={() => setRestoreConfirm(true)}>
              Restore
            </Btn>
          )}
        </div>
      )}

      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      <div className="nx-cc-tab-body">
        {tab === 'overview' && (
          <OverviewTab ws={ws} assignments={assignments} usage={usage} limits={limits} org={org} />
        )}
        {tab === 'assignments' && (
          <AssignmentsTab
            wsId={wsId} orgId={orgId}
            wsPerm={isArchived ? null : wsPerm}
            assignments={assignments} loadingAs={loadingAs}
            onAssignmentsChange={loadAssignments}
          />
        )}
        {tab === 'access' && (
          <AccessTab
            wsId={wsId}
            orgId={orgId}
            assignments={assignments}
            wsPerm={isArchived ? null : wsPerm}
            onAssignmentsChange={loadAssignments}
          />
        )}
        {tab === 'modules' && (
          <ModulesTab
            wsId={wsId} orgId={orgId} ws={ws}
            features={features ?? []}
            wsPerm={isArchived ? null : wsPerm}
            onModulesChange={loadWs}
          />
        )}
        {tab === 'settings' && (
          <SettingsTab ws={ws} orgId={orgId} wsPerm={isArchived ? null : wsPerm} onSaved={loadWs} />
        )}
        {tab === 'usage' && (
          <UsageTab usage={usage} limits={limits} assignments={assignments} />
        )}
        {tab === 'health' && (
          <HealthTab
            ws={ws} assignments={assignments} usage={usage} limits={limits} features={features ?? []}
            onNavigateAssignments={() => setTab('assignments')}
            onNavigateModules={() => setTab('modules')}
          />
        )}
        {tab === 'activity' && (
          <ActivityTab orgId={orgId} wsId={wsId} wsName={ws.name} />
        )}
      </div>

      {/* Rename modal */}
      {editModal && (
        <Modal
          title="Rename Workspace"
          icon="Pencil"
          onClose={() => setEditModal(false)}
          footer={<>
            <Btn variant="ghost" onClick={() => setEditModal(false)}>Cancel</Btn>
            <Btn variant="primary" icon="Check" disabled={editBusy || !editName.trim()} onClick={handleEdit}>
              {editBusy ? 'Saving…' : 'Save'}
            </Btn>
          </>}
        >
          <div className="nx-form" style={{ gap: 10 }}>
            <div className="nx-form-row">
              <label htmlFor="ws-rename-name">Name <span style={{ color: 'var(--nx-danger)' }}>*</span></label>
              <input id="ws-rename-name" className="nx-input" autoFocus value={editName}
                onChange={e => setEditName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleEdit(); }}
                placeholder="Workspace name" />
            </div>
          </div>
        </Modal>
      )}

      {archiveConfirm && (
        <ConfirmDialog
          title={'Archive — ' + ws.name}
          desc="Archive this workspace? All assigned members lose operational access. Organization member records are not affected. The workspace can be restored later."
          danger busy={false} confirmLabel="Archive Workspace"
          onConfirm={handleArchive} onCancel={() => setArchiveConfirm(false)}
        />
      )}

      {restoreConfirm && (
        <ConfirmDialog
          title={'Restore — ' + ws.name}
          desc="Restore this workspace? It will become active again and members will regain access."
          busy={restoreBusy} confirmLabel="Restore Workspace"
          onConfirm={handleRestore} onCancel={() => setRestoreConfirm(false)}
        />
      )}
    </div>
  );
}
